// Unpublished Work © 2023-2024 Deere & Company.
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Box } from "@mui/material";
import { createContext, ReactElement, useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";
import { useConfigContext } from "./ConfigContext";
import { DealerAccountContext } from "./DealerAccountContext";
import { BusinessAction, RBACRole } from "../api/rbac/types";
import ErrorPage from "../components/ErrorPage/ErrorPage";
import { CATEGORIES, TOGGLES } from "../pages/featureToggles/constants";
import { isAuthorizedCorporateUser } from "../pages/preferences/utils";
import { getShowClaimStatus } from "../utils/showClaims";
import { getShowCoreReturnStatus } from "../utils/showCoreReturns";
import {
  isAllInvoicesEnabled,
  isConsolidatedInvoicesEnabled,
} from "../utils/showInvoices";
import { isCreateReturnsEnabled, isReturnsEnabled } from "../utils/showReturns";
import { ErrorContext } from "./ErrorContext/ErrorContext";
import { useFeatureToggleContextV2 } from "./FeatureToggleContextV2/FeatureToggleContextV2";
import { RBACAccessContext } from "./RBACAccessContext/RBACAccessContext";
import { useUserContext } from "./UserContext/UserContext";

interface Props {
  setHasAccess: (value: boolean) => void | undefined;
  hasAccess: boolean | undefined;
}

enum PageName {
  CLAIMS,
  CREATE_CLAIM,
  CONSOLIDATE_INVOICES,
  INVOICES,
  HANDLING_UNIT,
  UNSPECIFIED,
  RETURNS,
  CORE_RETURNS,
  CORE_RETURNS_DETAILS,
  CREATE_RETURNS,
  CREATE_CORE_RETURNS,
  CREATE_CLAIM_BY_HU,
  ELIGIBILITIES,
  ALERTS_NOTIFICATIONS_CENTER,
  D2D,
}

export const AccessContext = createContext({} as Props);
export const useAccessContext = () => useContext(AccessContext);

export const AccessProvider = ({ children }: { children: ReactElement }) => {
  const location = useLocation();
  const { t } = useTranslation("common");
  const [hasAccess, setHasAccess] = useState<boolean>(true);
  const {
    ENABLE_FEATURE_TOGGLE_CONTEXT_V2,
    ENABLE_CORPORATE_VIEW_DPA_ROLE,
    ENABLE_JDPS_POSTSHIP_ACTIVITIES,
    ENABLE_JDPS_SURPLUS_RETURN_ACTIVITIES,
  } = useConfigContext();
  const { isRBACEnabled, isCorporateViewEnabled } =
    useContext(RBACAccessContext);
  const { corporateManagerSetupIncomplete } = useContext(DealerAccountContext);
  const { isToggleEnabled } = useFeatureToggleContextV2();
  const user = useUserContext();
  const error = useContext(ErrorContext);

  const isRbacEnabled = () => isRBACEnabled;
  const isFeatureToggleV2Enabled = () => ENABLE_FEATURE_TOGGLE_CONTEXT_V2;

  function isNoRoleAssignedErrorOccurred(): boolean {
    const subTitles = error?.message?.subTitle ?? [];
    const hasNoRolesMapped = subTitles.includes(
      "authentication.failed.noRolesMapped"
    );
    return hasNoRolesMapped;
  }

  function rbacAccessCheck(businessAction: BusinessAction): boolean {
    if (!isRbacEnabled()) return true;
    return !!user?.isAuthorizedTo(businessAction);
  }

  function featureToggleCheck(
    featureName: string,
    toggleName: string
  ): boolean {
    return (
      !isFeatureToggleV2Enabled() || isToggleEnabled(featureName, toggleName)
    );
  }

  function canJDPSDealerAccess(): boolean {
    return (
      ENABLE_JDPS_POSTSHIP_ACTIVITIES &&
      featureToggleCheck(
        CATEGORIES.RETURNS,
        TOGGLES.ENABLE_JDPS_POSTSHIP_ACTIVITIES
      )
    );
  }

  function canJDPSDealerAccessSurplusReturns(): boolean {
    return (
      featureToggleCheck(CATEGORIES.RETURNS, TOGGLES.ENABLE_SURPLUS_RETURNS) &&
      ENABLE_JDPS_SURPLUS_RETURN_ACTIVITIES &&
      featureToggleCheck(
        CATEGORIES.RETURNS,
        TOGGLES.ENABLE_JDPS_SURPLUS_RETURN_ACTIVITIES
      )
    );
  }

  const isAuthorizedToCreateReturn =
    !isRbacEnabled() ||
    (isRbacEnabled() && user?.isAuthorizedTo(BusinessAction.CREATE_RETURN));

  const isCorporateManagerSetupIncomplete =
    isCorporateViewEnabled &&
    isRBACEnabled &&
    isAuthorizedCorporateUser(user, ENABLE_CORPORATE_VIEW_DPA_ROLE) &&
    corporateManagerSetupIncomplete;

  const getParentPage = (): PageName => {
    const currentPath = location.pathname.toLowerCase();
    if (currentPath.startsWith("/claims")) {
      return PageName.CLAIMS;
    }

    if (currentPath.includes("/claim/create-by-hu")) {
      return PageName.CREATE_CLAIM_BY_HU;
    }

    if (currentPath.startsWith("/claim/create")) {
      return PageName.CREATE_CLAIM;
    }

    if (currentPath.startsWith("/invoices/download")) {
      return PageName.CONSOLIDATE_INVOICES;
    }

    if (currentPath.startsWith("/invoices")) {
      return PageName.INVOICES;
    }

    if (currentPath.includes("/handlingunits")) {
      return PageName.HANDLING_UNIT;
    }

    if (currentPath.match("\\/returns\\/core\\/[a-zA-Z0-9]+")) {
      return PageName.CORE_RETURNS_DETAILS;
    }

    if (currentPath.includes("/returns/core")) {
      return PageName.CORE_RETURNS;
    }

    if (currentPath.includes("/returns/createcorereturns")) {
      return PageName.CREATE_CORE_RETURNS;
    }

    if (currentPath.match("\\/returns\\/create(?![\\w\\d])")) {
      return PageName.CREATE_RETURNS;
    }

    if (currentPath.includes("/returns")) {
      return PageName.RETURNS;
    }

    if (currentPath.includes("/eligibilities")) {
      return PageName.ELIGIBILITIES;
    }

    if (currentPath.includes("/alerts-notifications-center")) {
      return PageName.ALERTS_NOTIFICATIONS_CENTER;
    }

    if (
      currentPath.includes("/my-locator-entry") ||
      currentPath.includes("/my-locator-inventory") ||
      currentPath.includes("/other-dealer-inventory") ||
      currentPath.includes("/d2d-orders") ||
      currentPath.includes("/d2d-requests")
    ) {
      return PageName.D2D;
    }

    return PageName.UNSPECIFIED;
  };

  const getErrorPageVerbiage = (
    parentPage: PageName
  ): { title: string; subtitles: string[] } => {
    switch (parentPage) {
      case PageName.CLAIMS: {
        return {
          title: t("unAuthorizedErrorPage.title"),
          subtitles: [t("unAuthorizedErrorPage.claims.subTitle")],
        };
      }
      case PageName.CREATE_CLAIM:
      case PageName.CREATE_CLAIM_BY_HU: {
        return {
          title: t("unAuthorizedErrorPage.title"),
          subtitles: [t("unAuthorizedErrorPage.createClaim.subTitle")],
        };
      }
      case PageName.CONSOLIDATE_INVOICES: {
        return {
          title: t("unAuthorizedErrorPage.title"),
          subtitles: [t("unAuthorizedErrorPage.consolidateInvoices.subTitle")],
        };
      }
      case PageName.INVOICES: {
        return {
          title: t("unAuthorizedErrorPage.title"),
          subtitles: [t("unAuthorizedErrorPage.allInvoices.subTitle")],
        };
      }
      case PageName.HANDLING_UNIT: {
        return {
          title: t("unAuthorizedErrorPage.title"),
          subtitles: [t("unAuthorizedErrorPage.handlingUnit.subTitle")],
        };
      }
      case PageName.RETURNS: {
        return {
          title: t("unAuthorizedErrorPage.returns.title"),
          subtitles: [t("unAuthorizedErrorPage.returns.subTitle")],
        };
      }
      case PageName.CORE_RETURNS: {
        return {
          title: t("unAuthorizedErrorPage.returns.title"),
          subtitles: [t("unAuthorizedErrorPage.coreReturns.subTitle")],
        };
      }
      case PageName.CREATE_RETURNS: {
        return {
          title: t("unAuthorizedErrorPage.returns.title"),
          subtitles: [t("unAuthorizedErrorPage.create.returns.subTitle")],
        };
      }
      case PageName.CORE_RETURNS_DETAILS: {
        return {
          title: t("unAuthorizedErrorPage.returns.title"),
          subtitles: [t("unAuthorizedErrorPage.coreReturnsDetails.subTitle")],
        };
      }
      case PageName.CREATE_CORE_RETURNS: {
        return {
          title: t("unAuthorizedErrorPage.returns.title"),
          subtitles: [t("unAuthorizedErrorPage.create.coreReturns.subTitle")],
        };
      }
      case PageName.ELIGIBILITIES: {
        return {
          title: t("unAuthorizedErrorPage.title"),
          subtitles: [t("unAuthorizedErrorPage.eligibilities.subTitle")],
        };
      }
      case PageName.ALERTS_NOTIFICATIONS_CENTER: {
        return {
          title: t("unAuthorizedErrorPage.title"),
          subtitles: [t("unAuthorizedErrorPage.alertAndNotification.subTitle")],
        };
      }
      case PageName.D2D: {
        return {
          title: t("unAuthorizedErrorPage.title"),
          subtitles: [t("unAuthorizedErrorPage.d2d.subTitle")],
        };
      }
      default: {
        return { title: "", subtitles: [] };
      }
    }
  };

  function canRenderClaims(businessAction: BusinessAction) {
    if (!rbacAccessCheck(businessAction)) return false;
    if (user?.is(RBACRole.JDPS_SERVICE_DEALER)) {
      if (
        isFeatureToggleV2Enabled() &&
        !isToggleEnabled(CATEGORIES.CLAIMS_SERVICE, TOGGLES.ENABLE_CLAIMS)
      ) {
        return false;
      }
      return !ENABLE_JDPS_POSTSHIP_ACTIVITIES || canJDPSDealerAccess();
    }
    if (isFeatureToggleV2Enabled())
      return isToggleEnabled(CATEGORIES.CLAIMS_SERVICE, TOGGLES.ENABLE_CLAIMS);
    return isRbacEnabled() || getShowClaimStatus();
  }

  function canRenderInvoices() {
    if (!isRbacEnabled()) return isAllInvoicesEnabled();
    if (user?.is(RBACRole.JDPS_SERVICE_DEALER)) {
      if (
        isFeatureToggleV2Enabled() &&
        !isToggleEnabled(CATEGORIES.INVOICE_SERVICE, TOGGLES.ENABLE_INVOICES)
      ) {
        return false;
      }
      return (
        rbacAccessCheck(BusinessAction.VIEW_INVOICES) &&
        ENABLE_JDPS_POSTSHIP_ACTIVITIES &&
        canJDPSDealerAccess()
      );
    }
    return (
      rbacAccessCheck(BusinessAction.VIEW_INVOICES) &&
      featureToggleCheck(CATEGORIES.INVOICE_SERVICE, TOGGLES.ENABLE_INVOICES)
    );
  }

  function canRenderHandlingUnits() {
    if (!rbacAccessCheck(BusinessAction.VIEW_HANDLING_UNITS)) return false;
    if (user?.is(RBACRole.JDPS_SERVICE_DEALER)) {
      if (!isFeatureToggleEnabledForHUs(TOGGLES.ENABLE_HANDLING_UNITS))
        return false;
      return !ENABLE_JDPS_POSTSHIP_ACTIVITIES || canJDPSDealerAccess();
    }
    if (!isFeatureToggleEnabledForHUs(TOGGLES.ENABLE_HANDLING_UNITS))
      return false;
    return isRbacEnabled() || getShowClaimStatus();
  }

  function canRenderReturns() {
    if (!rbacAccessCheck(BusinessAction.VIEW_RETURNS)) return false;
    if (user?.is(RBACRole.JDPS_SERVICE_DEALER)) {
      if (!isFeatureToggleEnabledForReturns(TOGGLES.ENABLE_RETURNS))
        return false;
      return canJDPSDealerAccess() || canJDPSDealerAccessSurplusReturns();
    }
    if (!isFeatureToggleEnabledForReturns(TOGGLES.ENABLE_RETURNS)) return false;
    return isRbacEnabled() || isReturnsEnabled();
  }

  function canRenderAlertsAndNotification() {
    return isToggleEnabled(
      CATEGORIES.ALERTS_SERVICE,
      TOGGLES.ENABLE_ALERTS_AND_NOTIFICATIONS
    );
  }

  function canRenderD2D() {
    return isToggleEnabled(CATEGORIES.D2D_SERVICE, TOGGLES.ENABLE_D2D);
  }

  function canRenderCoreReturns() {
    const showCoreReturnStatus = getShowCoreReturnStatus();
    if (!rbacAccessCheck(BusinessAction.VIEW_RETURNS)) return false;
    if (showCoreReturnStatus !== undefined) return showCoreReturnStatus;
    return isFeatureToggleEnabledForCoreReturns(TOGGLES.ENABLE_CORE_RETURNS);
  }

  function canRenderCreateCoreReturns() {
    const showCoreReturnStatusLegacyToggleValue = getShowCoreReturnStatus();
    const showCoreReturn =
      showCoreReturnStatusLegacyToggleValue !== undefined
        ? showCoreReturnStatusLegacyToggleValue
        : isFeatureToggleEnabledForReturns(TOGGLES.ENABLE_CORE_RETURNS);
    return (
      (isRbacEnabled() &&
        (isNoRoleAssignedErrorOccurred() ||
          (isAuthorizedToCreateReturn && showCoreReturn))) ||
      (!isRbacEnabled() && showCoreReturn)
    );
  }

  function isFeatureToggleEnabledForReturns(toggle: string) {
    return isRbacEnabled() ? isToggleEnabled(CATEGORIES.RETURNS, toggle) : true;
  }

  function isFeatureToggleEnabledForCoreReturns(toggle: string) {
    return isRbacEnabled() ? isToggleEnabled(CATEGORIES.RETURNS, toggle) : true;
  }

  function isFeatureToggleEnabledForHUs(toggle: string) {
    return ENABLE_FEATURE_TOGGLE_CONTEXT_V2 && isRbacEnabled()
      ? isToggleEnabled(CATEGORIES.HANDLING_UNIT_SERVICE, toggle)
      : true;
  }

  function canRenderEligibilities() {
    if (!rbacAccessCheck(BusinessAction.VIEW_ELIGIBILITIES)) return false;
    if (!isFeatureToggleEnabledForCore(TOGGLES.ENABLE_CORE_ELIGIBILITIES))
      return false;
    return isRbacEnabled();
  }

  function isFeatureToggleEnabledForCore(toggle: string) {
    return ENABLE_FEATURE_TOGGLE_CONTEXT_V2 && isRbacEnabled()
      ? isToggleEnabled(CATEGORIES.CORE_ELIGIBILITIES_SERVICE, toggle)
      : true;
  }

  function canRenderClaimsByHu() {
    if (
      !rbacAccessCheck(BusinessAction.VIEW_HANDLING_UNITS) ||
      !rbacAccessCheck(BusinessAction.CREATE_CLAIM)
    )
      return false;

    if (
      isFeatureToggleV2Enabled() &&
      !isToggleEnabled(CATEGORIES.CLAIMS_SERVICE, TOGGLES.ENABLE_CLAIMS_BY_HU)
    ) {
      return false;
    }

    if (user?.is(RBACRole.JDPS_SERVICE_DEALER)) {
      if (
        isFeatureToggleV2Enabled() &&
        !isToggleEnabled(CATEGORIES.CLAIMS_SERVICE, TOGGLES.ENABLE_CLAIMS)
      ) {
        return false;
      }
      return !ENABLE_JDPS_POSTSHIP_ACTIVITIES || canJDPSDealerAccess();
    }

    return true;
  }

  const renderContent = () => {
    const parentPage = getParentPage();
    let shouldRenderAccessError = false;

    switch (parentPage) {
      case PageName.CREATE_CLAIM: {
        shouldRenderAccessError = !canRenderClaims(BusinessAction.CREATE_CLAIM);
        break;
      }
      case PageName.CLAIMS: {
        shouldRenderAccessError = !canRenderClaims(BusinessAction.VIEW_CLAIMS);
        break;
      }
      case PageName.INVOICES: {
        shouldRenderAccessError = !canRenderInvoices();
        break;
      }
      case PageName.CONSOLIDATE_INVOICES: {
        if (isRbacEnabled() || !isConsolidatedInvoicesEnabled()) {
          shouldRenderAccessError = true;
        }
        break;
      }
      case PageName.HANDLING_UNIT: {
        shouldRenderAccessError = !canRenderHandlingUnits();
        break;
      }
      case PageName.CREATE_CLAIM_BY_HU: {
        shouldRenderAccessError = !canRenderClaimsByHu();
        break;
      }
      case PageName.RETURNS: {
        if (isRbacEnabled() && isNoRoleAssignedErrorOccurred()) {
          break;
        }
        shouldRenderAccessError = !canRenderReturns();
        break;
      }
      case PageName.CORE_RETURNS: {
        if (isRbacEnabled() && isNoRoleAssignedErrorOccurred()) {
          break;
        }
        shouldRenderAccessError = !canRenderCoreReturns();
        break;
      }
      case PageName.CORE_RETURNS_DETAILS: {
        if (isRbacEnabled() && isNoRoleAssignedErrorOccurred()) {
          break;
        }
        shouldRenderAccessError = !canRenderCoreReturns();
        break;
      }
      case PageName.ELIGIBILITIES: {
        shouldRenderAccessError = !canRenderEligibilities();
        break;
      }
      case PageName.CREATE_RETURNS: {
        if (
          (isRbacEnabled() &&
            (isNoRoleAssignedErrorOccurred() ||
              (isAuthorizedToCreateReturn &&
                isFeatureToggleEnabledForReturns(TOGGLES.ENABLE_RETURNS) &&
                isFeatureToggleEnabledForReturns(
                  TOGGLES.ENABLE_CREATE_RETURNS
                )))) ||
          (!isRbacEnabled() && isCreateReturnsEnabled())
        ) {
          break;
        } else {
          shouldRenderAccessError = true;
          break;
        }
      }
      case PageName.CREATE_CORE_RETURNS: {
        if (canRenderCreateCoreReturns()) {
          break;
        } else {
          shouldRenderAccessError = true;
          break;
        }
      }
      case PageName.ALERTS_NOTIFICATIONS_CENTER: {
        if (canRenderAlertsAndNotification()) {
          break;
        } else {
          shouldRenderAccessError = true;
          break;
        }
      }
      case PageName.D2D: {
        if (canRenderD2D()) {
          break;
        } else {
          shouldRenderAccessError = true;
          break;
        }
      }
      default:
        break;
    }
    if (
      (!hasAccess || shouldRenderAccessError) &&
      !isCorporateManagerSetupIncomplete // prevent rendering the error about access here - handled on my-dashboard with new corporate manager setup error
    ) {
      const { title, subtitles } = getErrorPageVerbiage(parentPage);

      return <NoAccessError title={title} subtitles={subtitles} />;
    }

    return children;
  };

  return (
    <AccessContext.Provider
      value={{
        hasAccess,
        setHasAccess,
      }}
    >
      {renderContent()}
    </AccessContext.Provider>
  );
};

export const NoAccessError = ({
  title,
  subtitles,
  showReturnHomeButton = true,
}: {
  title: string;
  subtitles: string[];
  showReturnHomeButton?: boolean;
}) => (
  <Box
    sx={{ display: "flex", height: "70vh", justifyContent: "center" }}
    data-testid="unauthorizedError"
  >
    <ErrorPage
      title={title}
      subtitles={subtitles}
      icon={{ Component: WarningAmberIcon, color: "warning" }}
      showLoginBtn={false}
      showBackToHomeLink={false}
      showReturnHomeButton={showReturnHomeButton}
    />
  </Box>
);
